import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { X, Clock } from "lucide-react";

interface ScheduleHeaderProps {
  turnTimeMinutes: number;
  onTurnTimeChange: (value: number) => void;
  unassignedCount: number;
  assignedCount: number;
  onAssign: () => void;
  onClear: () => void;
  isAssigning: boolean;
  isClearing: boolean;
  extraControls?: React.ReactNode;
  idPrefix?: string;
  hideActions?: boolean;
}

export function ScheduleHeader({
  turnTimeMinutes,
  onTurnTimeChange,
  unassignedCount,
  assignedCount,
  onAssign,
  onClear,
  isAssigning,
  isClearing,
  extraControls,
  idPrefix = "schedule",
  hideActions = false,
}: ScheduleHeaderProps) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {!hideActions && (
        <ScheduleActions
          turnTimeMinutes={turnTimeMinutes}
          onTurnTimeChange={onTurnTimeChange}
          unassignedCount={unassignedCount}
          assignedCount={assignedCount}
          onAssign={onAssign}
          onClear={onClear}
          isAssigning={isAssigning}
          isClearing={isClearing}
          idPrefix={idPrefix}
        />
      )}
      {extraControls}
    </div>
  );
}

export interface ScheduleActionsProps {
  turnTimeMinutes: number;
  onTurnTimeChange: (value: number) => void;
  unassignedCount: number;
  assignedCount: number;
  onAssign: () => void;
  onClear: () => void;
  isAssigning: boolean;
  isClearing: boolean;
  idPrefix?: string;
}

export function ScheduleActions({
  turnTimeMinutes,
  onTurnTimeChange,
  unassignedCount,
  assignedCount,
  onAssign,
  onClear,
  isAssigning,
  isClearing,
  idPrefix = "schedule",
}: ScheduleActionsProps) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex items-center gap-1.5">
        <Clock className="h-4 w-4 text-muted-foreground" />
        <Label htmlFor={`${idPrefix}-turn-time`} className="text-sm text-muted-foreground whitespace-nowrap">
          Turn:
        </Label>
        <Input
          id={`${idPrefix}-turn-time`}
          type="number"
          value={turnTimeMinutes}
          onChange={(e) => onTurnTimeChange(parseInt(e.target.value) || 0)}
          className="w-16 h-8 text-sm"
          min={0}
        />
        <span className="text-sm text-muted-foreground">min</span>
      </div>

      {unassignedCount > 0 && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="sm" onClick={onAssign} disabled={isAssigning}>
                {isAssigning ? "Assigning..." : `Assign (${unassignedCount})`}
              </Button>
            </TooltipTrigger>
            <TooltipContent>Assign tractors and operators to orders</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      {assignedCount > 0 && (
        <Button
          variant="outline"
          size="sm"
          onClick={onClear}
          disabled={isClearing}
          className="text-destructive hover:text-destructive"
        >
          <X className="h-4 w-4 mr-1" />
          {isClearing ? "Clearing..." : `Clear (${assignedCount})`}
        </Button>
      )}
    </div>
  );
}
